import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Download, Upload, Database, Shield, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";

export default function AdminBackup({ onBack }) {
  const [adminPassword, setAdminPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [backupData, setBackupData] = useState(null);

  // Fetch database stats on load
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/stats`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleAuthenticate = () => {
    if (adminPassword === "BeybladeAdmin2024!") {
      setIsAuthenticated(true);
      setMessage({ type: "success", text: "Admin access granted!" });
    } else {
      setMessage({ type: "error", text: "Invalid admin password" });
    }
  };

  const handleExportBackup = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await axios.post(`${API}/admin/backup/export`, {
        admin_password: adminPassword
      });
      
      if (response.data.success) {
        setBackupData(response.data.backup);
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(response.data.backup, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `beyblade_backup_${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        setMessage({ type: "success", text: "Backup exported and downloaded!" });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.detail || "Export failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setLoading(true);
    setMessage(null);
    
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      // Confirm before import
      const confirm = window.confirm(
        `⚠️ WARNING: This will REPLACE all current data!\n\n` +
        `Backup from: ${backupData.backup_timestamp || "unknown"}\n` +
        `Collections: ${Object.keys(backupData.collections || {}).join(", ")}\n\n` +
        `Are you sure you want to proceed?`
      );
      
      if (!confirm) {
        setLoading(false);
        return;
      }
      
      const response = await axios.post(`${API}/admin/backup/import`, {
        admin_password: adminPassword,
        backup_data: backupData
      });
      
      if (response.data.success) {
        setMessage({ type: "success", text: "Backup imported successfully! Refresh the app to see changes." });
        fetchStats(); // Refresh stats
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setMessage({ type: "error", text: "Invalid JSON file" });
      } else {
        setMessage({ type: "error", text: error.response?.data?.detail || "Import failed" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={onBack} className="text-white">
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Database className="h-6 w-6 text-blue-400" />
          Database Backup
        </h1>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === "success" ? "bg-green-900/50 text-green-300" : "bg-red-900/50 text-red-300"
        }`}>
          {message.type === "success" ? <CheckCircle className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          {message.text}
        </div>
      )}

      {/* Stats Card */}
      {stats && (
        <Card className="mb-4 bg-gray-800/50 border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">Current Database Stats</CardTitle>
            <CardDescription className="text-gray-400">DB: {stats.db_name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(stats.collection_stats || {}).map(([name, count]) => (
                <div key={name} className="flex justify-between text-sm p-2 bg-gray-700/50 rounded">
                  <span className="text-gray-300">{name}</span>
                  <span className="text-blue-400 font-mono">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Authentication */}
      {!isAuthenticated ? (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-yellow-400" />
              Admin Authentication
            </CardTitle>
            <CardDescription className="text-gray-400">
              Enter admin password to access backup features
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="password"
              placeholder="Admin password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
            />
            <Button onClick={handleAuthenticate} className="w-full bg-blue-600 hover:bg-blue-700">
              Authenticate
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Export Card */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Download className="h-5 w-5 text-green-400" />
                Export Backup
              </CardTitle>
              <CardDescription className="text-gray-400">
                Download a full backup of your database as JSON
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleExportBackup} 
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Export & Download Backup
              </Button>
            </CardContent>
          </Card>

          {/* Import Card */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Upload className="h-5 w-5 text-orange-400" />
                Import Backup
              </CardTitle>
              <CardDescription className="text-gray-400">
                ⚠️ Warning: This will REPLACE all current data!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label className="block">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportBackup}
                  disabled={loading}
                  className="hidden"
                />
                <Button 
                  as="span"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 cursor-pointer"
                  onClick={() => document.querySelector('input[type="file"]').click()}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Select Backup File to Import
                </Button>
              </label>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="bg-blue-900/30 border-blue-700">
            <CardContent className="pt-4">
              <h3 className="text-blue-300 font-semibold mb-2">📋 How to Use</h3>
              <ol className="text-sm text-blue-200 space-y-1 list-decimal list-inside">
                <li>Export a backup BEFORE deploying to production</li>
                <li>Save the JSON file securely</li>
                <li>If you lose data, import the backup to restore</li>
                <li>Regular backups are recommended!</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
